import React, { useState, useEffect } from 'react';
import styles from './PostGameModal.module.css';
import { GameResult } from '../../services/MMR/types';
import { PieceType } from '../../services/zodiacEngine/types';

type WinCondition = 'kingslayer' | 'race' | 'timeout' | 'threefold' | 'no_moves' | 'draw' | 'surrender' | 'forfeit';

interface PostGameModalProps {
  isOpen: boolean;
  gameResult: GameResult | null;
  onClose: () => void;
  onRematch?: () => void;
  onSearchAgain?: () => void;
  onBackToLobby?: () => void;
  // Layout prop - when true, renders inline without fixed overlay
  isInline?: boolean;
  // King reveal props
  winCondition?: WinCondition;
  myKing?: PieceType;
  opponentKing?: PieceType;
  isLightPlayer?: boolean;
  myKingGuess?: PieceType;
  // Request-based reveal flow
  iHaveRequestedReveal?: boolean;
  opponentRequestedReveal?: boolean;
  myRequestResponse?: 'accepted' | 'declined' | null;
  opponentRequestResponse?: 'accepted' | 'declined' | null;
  onRequestKingReveal?: () => void;
  onRespondToKingReveal?: (accepted: boolean) => void;
}

export default function PostGameModal({
  isOpen,
  gameResult,
  onClose,
  onRematch,
  onSearchAgain,
  onBackToLobby,
  isInline = false,
  winCondition,
  myKing,
  opponentKing,
  isLightPlayer,
  myKingGuess,
  iHaveRequestedReveal,
  opponentRequestedReveal,
  myRequestResponse,
  opponentRequestResponse,
  onRequestKingReveal,
  onRespondToKingReveal,
}: PostGameModalProps) {
  const [showReveal, setShowReveal] = useState(false);
  const [revealStage, setRevealStage] = useState(0);
  const [revealAnimation, setRevealAnimation] = useState<'idle' | 'suspense' | 'revealed'>('idle');

  useEffect(() => {
    // Check if this is the final placement game (game 10)
    const isFinalPlacementGame = isOpen && gameResult &&
      gameResult.gameMode === 'placement' &&
      gameResult.placementGamesCompleted === 10;

    if (isFinalPlacementGame) {
      // Final placement reveal - start animation after short delay
      const timer = setTimeout(() => {
        setShowReveal(true);
        // Stage 1: "Calculating..." (brief)
        setTimeout(() => setRevealStage(1), 800);
        // Stage 2: Show rating with reveal animation
        setTimeout(() => setRevealStage(2), 2200);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isOpen, gameResult]);

  // Handle opponent king reveal animation when they accept our request
  useEffect(() => {
    if (myRequestResponse === 'accepted') {
      setRevealAnimation('suspense');
      const timer = setTimeout(() => {
        setRevealAnimation('revealed');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [myRequestResponse]);

  // Reset animation state when modal closes/opens
  useEffect(() => {
    if (!isOpen) {
      setRevealAnimation('idle');
      setRevealStage(0);
      setShowReveal(false);
    }
  }, [isOpen]);

  if (!isOpen || !gameResult) return null;

  const isPlacement = gameResult.gameMode === 'placement';
  const isCasual = gameResult.gameMode === 'casual';
  const isWin = gameResult.result === 'win';
  const isDraw = gameResult.result === 'draw';
  const isLoss = gameResult.result === 'loss';

  // Format win condition for display
  const getWinConditionText = () => {
    if (!winCondition) return null;
    switch (winCondition) {
      case 'kingslayer': return 'by Kingslayer';
      case 'race': return 'by King Race';
      case 'timeout': return 'by Opponent time-out';
      case 'surrender': return 'by Surrender';
      case 'forfeit': return 'by Opponent forfeit';
      case 'threefold': return 'Threefold Repetition';
      case 'no_moves': return 'No Legal Moves';
      case 'draw': return 'Draw';
      default: return null;
    }
  };
  const winConditionText = getWinConditionText();

  // Check if this is the final placement game (game 10)
  const isFinalPlacement = isPlacement && gameResult.placementGamesCompleted === 10;

  const renderPlacementResult = () => {
    if (isFinalPlacement) {
      // Special reveal for final placement
      return (
        <div className={styles.finalPlacementReveal}>
          {revealStage === 0 && (
            <div className={styles.revealStage1}>
              <div className={styles.spinner}></div>
              <h2>Analyzing placement matches...</h2>
            </div>
          )}
          {revealStage === 1 && (
            <div className={styles.revealStage2}>
              <div className={styles.calculatingAnimation}>
                <span className={styles.numberFlip}>####</span>
              </div>
              <h2>Calculating your starting ELO...</h2>
            </div>
          )}
          {revealStage === 2 && (
            <div className={styles.revealStage3}>
              <div className={styles.ratingRevealContainer}>
                <h1 className={styles.congratulations}>Congratulations</h1>
                <h2 className={styles.placementComplete}>
                  {gameResult.timeControl
                    ? `${gameResult.timeControl.charAt(0).toUpperCase() + gameResult.timeControl.slice(1)} Placement Complete`
                    : 'Placement Complete'}
                </h2>
                <div className={styles.eloReveal}>
                  <span className={styles.eloLabel}>Your Starting ELO</span>
                  <span className={styles.eloNumber}>{gameResult.ratingAfter}</span>
                </div>
                {gameResult.ratingAfter >= 1400 && (
                  <p className={styles.encouragement}>Outstanding performance</p>
                )}
                {gameResult.ratingAfter >= 1300 && gameResult.ratingAfter < 1400 && (
                  <p className={styles.encouragement}>Great start</p>
                )}
                {gameResult.ratingAfter < 1300 && (
                  <p className={styles.encouragement}>Your journey begins</p>
                )}
              </div>
            </div>
          )}
        </div>
      );
    }

    // Regular placement game (not final)
    const gamesCompleted = gameResult.placementGamesCompleted || 0;
    const timeControlLabel = gameResult.timeControl
      ? `${gameResult.timeControl.charAt(0).toUpperCase() + gameResult.timeControl.slice(1)} `
      : '';
    return (
      <div className={styles.placementContent}>
        <h2 className={styles.placementTitle}>{timeControlLabel}Placement Match Complete</h2>
        <div className={styles.placementProgress}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${(gamesCompleted / 10) * 100}%` }}
            />
          </div>
          <span className={styles.progressText}>
            {gamesCompleted} / 10 Games Completed
          </span>
        </div>
        <div className={styles.matchResult}>
          <span className={`${styles.resultBadge} ${
            isWin ? styles.win : isDraw ? styles.draw : styles.loss
          }`}>
            {isWin ? 'Victory' : isDraw ? 'Draw' : 'Defeat'}
          </span>
          {winConditionText && (
            <span className={styles.winConditionText}>{winConditionText}</span>
          )}
        </div>
        <p className={styles.placementInfo}>
          Keep playing to complete your placement matches and unlock your ELO rating!
        </p>
      </div>
    );
  };

  const renderRankedResult = () => {
    const ratingChange = gameResult.ratingChange;
    const isPositive = ratingChange > 0;
    const isNeutral = ratingChange === 0;

    return (
      <div className={styles.rankedContent}>
        <h2 className={styles.rankedTitle}>Ranked Match Complete</h2>
        <div className={styles.matchResult}>
          <span className={`${styles.resultBadge} ${
            isWin ? styles.win : isDraw ? styles.draw : styles.loss
          }`}>
            {isWin ? 'Victory' : isDraw ? 'Draw' : 'Defeat'}
          </span>
          {winConditionText && (
            <span className={styles.winConditionText}>{winConditionText}</span>
          )}
        </div>
        <div className={styles.eloChange}>
          <div className={styles.eloRow}>
            <span className={styles.eloLabel}>Previous Rating:</span>
            <span className={styles.eloValue}>{gameResult.ratingBefore}</span>
          </div>
          <div className={styles.eloChangeArrow}>
            {isPositive ? '↑' : isNeutral ? '→' : '↓'}
          </div>
          <div className={styles.eloRow}>
            <span className={styles.eloLabel}>New Rating:</span>
            <span className={`${styles.eloValue} ${
              isPositive ? styles.eloIncrease : isNeutral ? styles.eloNeutral : styles.eloDecrease
            }`}>
              {gameResult.ratingAfter}
            </span>
          </div>
        </div>
        <div className={`${styles.ratingDelta} ${
          isPositive ? styles.deltaPositive : isNeutral ? styles.deltaNeutral : styles.deltaNegative
        }`}>
          {isPositive ? '+' : ''}{ratingChange} ELO
        </div>
      </div>
    );
  };

  const renderCasualResult = () => {

    return (
      <div className={styles.casualContent}>
        <h2 className={styles.casualTitle}>Casual Match Complete</h2>
        <div className={styles.matchResult}>
          <span className={`${styles.resultBadge} ${
            isWin ? styles.win : isDraw ? styles.draw : styles.loss
          }`}>
            {isWin ? 'Victory' : isDraw ? 'Draw' : 'Defeat'}
          </span>
          {winConditionText && (
            <span className={styles.winConditionText}>{winConditionText}</span>
          )}
        </div>
        <div className={styles.noEloChange}>
          No ELO Changes Applied
        </div>
      </div>
    );
  };

  // Helper to get piece icon path
  const getPieceIconPath = (pieceType: PieceType, isLight: boolean) => {
    const pieceName = pieceType.toLowerCase();
    const color = isLight ? 'light' : 'dark';
    return `/Icons/${pieceName}-${color}.png`;
  };

  // Helper to format piece name for display
  const formatPieceName = (pieceType: PieceType) => {
    return pieceType.charAt(0).toUpperCase() + pieceType.slice(1).toLowerCase();
  };

  const renderKingReveal = () => {
    // Don't render if no king data available
    if (!myKing || !opponentKing || isLightPlayer === undefined) {
      return null;
    }

    const opponentIsLight = !isLightPlayer;

    // Auto-reveal for kingslayer (opponent already knows which piece was captured)
    if (winCondition === 'kingslayer') {
      return (
        <div className={styles.kingRevealSection}>
          <div className={styles.kingRevealDivider}>
            <span>King Captured</span>
          </div>
          <div className={styles.kingAutoReveal}>
            <img
              src={getPieceIconPath(opponentKing, opponentIsLight)}
              alt={opponentKing}
              className={styles.kingIcon}
            />
            <p className={styles.kingRevealText}>
              {isWin ? `You captured their ${formatPieceName(opponentKing)}!` : `Your ${formatPieceName(myKing)} was captured!`}
            </p>
          </div>
        </div>
      );
    }

    // Request-based reveal for race/timeout/draw/surrender
    return (
      <div className={styles.kingRevealSection}>
        <div className={styles.kingRevealDivider}>
          <span>Reveal King</span>
        </div>

        {/* Outbound request: ask to see opponent's king */}
        <div className={styles.outboundRequest}>
          {!iHaveRequestedReveal ? (
            <button
              className={styles.requestRevealButton}
              onClick={onRequestKingReveal}
              disabled={!onRequestKingReveal}
            >
              Request King Reveal
            </button>
          ) : myRequestResponse === null ? (
            <div className={styles.pendingResponse}>
              <div className={styles.suspenseIcon}>?</div>
              <p className={styles.pendingText}>Waiting for opponent...</p>
            </div>
          ) : myRequestResponse === 'declined' ? (
            <p className={styles.declinedText}>Opponent declined to reveal</p>
          ) : (
            <>
              {revealAnimation === 'suspense' && (
                <div className={styles.suspenseContainer}>
                  <div className={styles.suspenseIcon}>?</div>
                  <p className={styles.suspenseText}>Opponent accepted reveal...</p>
                </div>
              )}
              {revealAnimation === 'revealed' && (
                <div className={styles.opponentKingRevealed}>
                  <img
                    src={getPieceIconPath(opponentKing, opponentIsLight)}
                    alt={opponentKing}
                    className={`${styles.kingIcon} ${styles.kingPopIn}`}
                  />
                  <p className={styles.kingRevealText}>
                    Their king: {formatPieceName(opponentKing)}
                  </p>
                  {myKingGuess && (
                    <p className={myKingGuess === opponentKing ? styles.guessCorrect : styles.guessIncorrect}>
                      {myKingGuess === opponentKing
                        ? 'Your guess was correct!'
                        : `You guessed ${formatPieceName(myKingGuess)}`}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Inbound request: opponent wants to see our king */}
        {opponentRequestedReveal && !opponentRequestResponse && (
          <div className={styles.incomingRequestPrompt}>
            <p className={styles.incomingRequestText}>Opponent wants to know your king</p>
            <div className={styles.revealResponseButtons}>
              <button
                className={styles.acceptRevealButton}
                onClick={() => onRespondToKingReveal?.(true)}
              >
                Reveal
              </button>
              <button
                className={styles.declineRevealButton}
                onClick={() => onRespondToKingReveal?.(false)}
              >
                Decline
              </button>
            </div>
          </div>
        )}
        {opponentRequestResponse === 'accepted' && (
          <div className={styles.myKingRevealed}>
            <img
              src={getPieceIconPath(myKing, isLightPlayer)}
              alt={myKing}
              className={styles.kingIcon}
            />
            <p className={styles.kingRevealText}>
              You revealed your king: {formatPieceName(myKing)}
            </p>
          </div>
        )}
        {opponentRequestResponse === 'declined' && (
          <p className={styles.declinedText}>You declined to reveal</p>
        )}
      </div>
    );
  };

  // Inline mode: render modal without fixed overlay
  if (isInline) {
    return (
      <div className={styles.inlineModalContainer}>
        <div className={styles.modal}>
          {isCasual ? renderCasualResult() : (isPlacement ? renderPlacementResult() : renderRankedResult())}

          {/* King Reveal Section */}
          {renderKingReveal()}

          {(!isFinalPlacement || revealStage === 2) && (
            <div className={styles.actions}>
              {onRematch && (
                <button className={styles.rematchButton} onClick={onRematch}>
                  Rematch
                </button>
              )}
              {onSearchAgain && (
                <button className={styles.searchButton} onClick={onSearchAgain}>
                  Search Again
                </button>
              )}
            </div>
          )}
          {onBackToLobby && (
            <div className={styles.actions}>
              <button className={styles.searchButton} onClick={onBackToLobby}>
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default: fixed overlay mode
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {isCasual ? renderCasualResult() : (isPlacement ? renderPlacementResult() : renderRankedResult())}

        {/* King Reveal Section */}
        {renderKingReveal()}

        {(!isFinalPlacement || revealStage === 2) && (
          <div className={styles.actions}>
            {onRematch && (
              <button className={styles.rematchButton} onClick={onRematch}>
                Rematch
              </button>
            )}
            {onSearchAgain && (
              <button className={styles.searchButton} onClick={onSearchAgain}>
                Search Again
              </button>
            )}
          </div>
        )}
        {onBackToLobby && (
          <div className={styles.actions}>
            <button className={styles.searchButton} onClick={onBackToLobby}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
